import { Applicant, RelationshipType } from 'features/Entity/Applicant'
import React, { useMemo, useState } from 'react'
import { store } from "app/store";
import {
  FieldValues,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form'
import {
  renderEmailWithPattern,
  renderInputText
} from '../../../components/Forms/FormsComponent'
import t, { changeLanguage } from '../../../components/I18N/TranslateHelpers'
import { GetCurrentLocale, SelectInterface } from '../../../components/Utils'
import SelectField from 'components/Forms/SelectField'
import { SingleValue } from 'react-select'
import { useSelector } from "react-redux";
import { RootState } from "app/RootReducer";
import { getEmergencyContactRelTypes } from 'features/Slice/ApplicantSlice';

interface ComponentProps {
  register: UseFormRegister<FieldValues>
  errors: { [x: string]: any }
  applicantData: Applicant
  isReadOnly: boolean
  setValue: UseFormSetValue<FieldValues>;
}

const ApplicantEmergencyInfoForm: React.FC<ComponentProps> = ({
  register,
  errors,
  applicantData,
  isReadOnly,
  setValue
}) => {
  const emergencyRelTypes = useSelector((state: RootState) => state.applicantRdc.emergencyContactRelTypes);
  const isRequestingEmergencyRelTypes = useSelector((state: RootState) => state.applicantRdc.isRequestingEmergencyContactRelTypes);

  const [selectedEmergencyRelType, setSelectedEmergencyRelType] = useState<RelationshipType|null|undefined>(undefined);

  const emergencyRelTypesSelect = useMemo(() =>
    emergencyRelTypes.map(e => {return {label: e.label, value: e.id} as SelectInterface})
  , [emergencyRelTypes]);

  const displayEmergencyRelTypeDetails = useMemo(() => {
    return selectedEmergencyRelType ? selectedEmergencyRelType.isDetailsNeeded : false
  }, [selectedEmergencyRelType]);

  React.useEffect(() => {
    const locale = GetCurrentLocale();
    changeLanguage(locale);
    
    store.dispatch(getEmergencyContactRelTypes());
  }, []);

  React.useEffect(() => {
    onEmergencyContactRelationshipTypeChange(applicantData.applicantProfile?.emergencyContactRelationshipTypeId ?? 0, false);
  }, [emergencyRelTypes]);

  function onEmergencyContactRelationshipTypeChange(newValId: number, updateDetailsField: boolean) {
    if(isNaN(newValId))
    {
      setSelectedEmergencyRelType(null);
      if(updateDetailsField)
      {
        setValue('applicantProfile.emergencyContactRelationshipTypeDetails', null);
      }
    }
    else
    {
      const selectedType = emergencyRelTypes.find(e => e.id === newValId);

      if(selectedType !== null && selectedType !== undefined)
      {
        setSelectedEmergencyRelType(selectedType);
        if(!selectedType.isDetailsNeeded && updateDetailsField)
        {
          setValue('applicantProfile.emergencyContactRelationshipTypeDetails', null);
        }
      }
      else
      {
        setSelectedEmergencyRelType(null);
        if(updateDetailsField)
        {
          setValue('applicantProfile.emergencyContactRelationshipTypeDetails', null);
        }
      }
    }
  }

  return (
    <>
      <input
        type='hidden'
        id='applicantProfile.id'
        value={applicantData.applicantProfile?.id ?? 0}
        {...register('applicantProfile.id')}
      />
      {renderInputText(
        t('emergency_contact_name'),
        'applicantProfile.emergencyContactName',
        applicantData.applicantProfile?.emergencyContactName,
        errors.applicantProfile?.emergencyContactName,
        register,
        true,
        'mb-3 col-md-6',
        false,
        applicantData.applicantProfile?.initializedFields?.includes(
          'EmergencyContactName'
        ) || isReadOnly
      )}
      {renderInputText(
        t('emergency_contact_phone'),
        'applicantProfile.emergencyContactHomePhoneNumber',
        applicantData.applicantProfile?.emergencyContactHomePhoneNumber,
        errors.applicantProfile?.emergencyContactHomePhoneNumber,
        register,
        false,
        'mb-3 col-md-6',
        false,
        applicantData.applicantProfile?.initializedFields?.includes(
          'EmergencyContactHomePhoneNumber'
        ) || isReadOnly
      )}
      {renderInputText(
        t('emergency_contact_mobile'),
        'applicantProfile.emergencyContactMobilePhoneNumber',
        applicantData.applicantProfile?.emergencyContactMobilePhoneNumber,
        errors.applicantProfile?.emergencyContactMobilePhoneNumber,
        register,
        true,
        'mb-3 col-md-6',
        false,
        applicantData.applicantProfile?.initializedFields?.includes(
          'EmergencyContactMobilePhoneNumber'
        ) || isReadOnly
      )}
      {
        emergencyRelTypesSelect.length > 0 ?
        <SelectField
          classes="col-md-6 mb-3"
          name='applicantProfile.emergencyContactRelationshipTypeId'
          options={emergencyRelTypesSelect}
          defaultValue={emergencyRelTypesSelect.find(e => e.value == applicantData.applicantProfile?.emergencyContactRelationshipTypeId) ?? null}
          multiple={false}
          label={t('relationship_to_student')}
          required={true}
          setValue={setValue}
          readOnly={applicantData.applicantProfile?.initializedFields?.includes('RelationshipToTheStudent') || isRequestingEmergencyRelTypes || isReadOnly}
          customOnChange={(newVal) => onEmergencyContactRelationshipTypeChange((newVal as SingleValue<SelectInterface>)?.value, true)}
        />
        : null
      }
      {
        displayEmergencyRelTypeDetails ?
        renderInputText(
          t('relationship_details'),
          'applicantProfile.emergencyContactRelationshipTypeDetails',
          applicantData.applicantProfile?.emergencyContactRelationshipTypeDetails,
          errors.applicantProfile?.emergencyContactRelationshipTypeDetails,
          register,
          true,
          'mb-3 col-md-6',
          false,
          applicantData.applicantProfile?.initializedFields?.includes(
            'EmergencyContactRelationshipTypeDetails'
          ) || isReadOnly
        )
        :
        null
      }
      {renderEmailWithPattern(
        t('emergency_contact_email'),
        applicantData.applicantProfile?.emergencyContactEmail,
        register,
        'applicantProfile.emergencyContactEmail',
        true,
        errors.applicantProfile?.emergencyContactEmail,
        'mb-3 col-md-6',
        applicantData.applicantProfile?.initializedFields?.includes(
          'EmergencyContactEmail'
        ) || isReadOnly
      )}
    </>
  )
}

export default ApplicantEmergencyInfoForm
